import { useState } from 'react';

/**
 * Hook personalizado para manejar filtros avanzados
 * Implementa un estado y métodos para gestionar filtros de manera limpia siguiendo el patrón de Custom Hooks
 * 
 * @returns {Object} Estado y funciones para manejo de filtros
 */
export const useFilters = (initialFilters = {}) => {
  // Estado por defecto con valores para todos los filtros posibles
  const defaultFilters = {
    startDate: null,
    endDate: null,
    paymentStatus: 'all',
    shippingStatus: 'all',
    ...initialFilters
  };
  
  // Estado local para los filtros
  const [filters, setFiltersState] = useState(defaultFilters);
  
  /**
   * Actualiza uno o más filtros
   * @param {Object} newFilters - Objeto con los filtros a actualizar
   */
  const setFilters = (newFilters) => {
    setFiltersState(prev => ({
      ...prev,
      ...newFilters
    }));
  };
  
  /**
   * Actualiza un único filtro
   * @param {string} key - Nombre del filtro
   * @param {any} value - Valor del filtro
   */
  const setFilter = (key, value) => {
    setFiltersState(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  /**
   * Reinicia todos los filtros a sus valores predeterminados
   */
  const resetFilters = () => {
    setFiltersState(defaultFilters);
  };
  
  /**
   * Verifica si algún filtro está aplicado (diferente del valor predeterminado)
   * @returns {boolean} True si hay algún filtro activo
   */
  const hasActiveFilters = () => {
    return (
      (filters.startDate !== null) || 
      (filters.endDate !== null) || 
      (filters.paymentStatus !== 'all') || 
      (filters.shippingStatus !== 'all')
    );
  };
  
  /**
   * Convierte los filtros actuales a parámetros URL
   * @returns {Object} Objeto con parámetros para query string
   */
  const toQueryParams = () => {
    const params = {};
    
    if (filters.startDate) {
      params.startDate = filters.startDate.toISOString().split('T')[0];
    }
    
    if (filters.endDate) {
      params.endDate = filters.endDate.toISOString().split('T')[0];
    }
    
    if (filters.paymentStatus && filters.paymentStatus !== 'all') {
      params.paymentStatus = filters.paymentStatus;
    }
    
    if (filters.shippingStatus && filters.shippingStatus !== 'all') {
      params.shippingStatus = filters.shippingStatus;
    }
    
    return params;
  };
  
  return {
    filters,
    setFilters,
    setFilter,
    resetFilters,
    hasActiveFilters,
    toQueryParams
  };
};
